"use client"

import React, { useState } from "react"
import { cn } from "../lib/utils"
import { useAuth } from '../contexts/AuthContext';
import { useVehicle } from '../contexts/VehicleContext';
import { ManageVehiclesDialog } from "./manage-vehicles";
import { SettingsDialog } from "../components/settings-dialog";
import { ConfirmationDialog } from "./confirmation-dialog";
import { ShareChatDialog } from "./share-chat-dialog";
import { PlusCircle, X, Menu, Trash, Edit, Check, ChevronDown, MoreVertical, Copy, Share } from "./icons";
import { useNavigate } from "react-router-dom";

// Mock UI components
const Button = ({ children, className, onClick, variant, size, ...props }: any) => (
  <button
    className={cn(
      "px-4 py-2 rounded font-medium focus:outline-none",
      variant === "ghost" && "bg-transparent hover:bg-zinc-700/50",
      variant === "outline" && "border border-zinc-700 bg-transparent",
      size === "icon" && "p-2 aspect-square",
      size === "sm" && "text-sm px-3 py-1",
      className,
    )}
    onClick={onClick}
    {...props}
  >
    {children}
  </button>
)

const ScrollArea = ({ children, className, ...props }: any) => (
  <div className={cn("overflow-auto sidebar-scroll", className)} {...props}>
    {children}
  </div>
)

interface SelectProps {
  value?: string;
  onValueChange: (value: string) => void;
  children: React.ReactNode;
  className?: string;
}

export const Select: React.FC<SelectProps> = ({ value, onValueChange, children, className }) => {
  const [isOpen, setIsOpen] = useState(false);

  const hasChildren = React.Children.toArray(children).some(
    (child) => React.isValidElement(child) && child.type === SelectItem
  );

  const handleOptionClick = (newValue: string) => {
    onValueChange(newValue);
    setIsOpen(false);
  };

  return (
    <div className={`relative ${className}`}>
      <button
        className={`w-full bg-zinc-800 border border-zinc-700 rounded p-2 text-left ${
          hasChildren ? "text-white" : "text-zinc-500 cursor-not-allowed"
        }`}
        onClick={() => hasChildren && setIsOpen(!isOpen)}
        disabled={!hasChildren}
      >
        {value || (hasChildren ? "Select a vehicle" : "No saved vehicles")}
        {hasChildren && (
          <span className="absolute inset-y-0 right-0 flex items-center pr-3 pointer-events-none">
            <ChevronDown />
          </span>
        )}
      </button>

      {isOpen && hasChildren && (
        <ul className="absolute z-10 w-full bg-zinc-800 border border-zinc-700 rounded mt-1">
          {React.Children.map(children, (child) => {
            if (React.isValidElement(child) && child.type === SelectItem) {
              const childElement = child as React.ReactElement<SelectItemProps>;
              return React.cloneElement(childElement, {
                onClick: () => handleOptionClick(childElement.props.value),
              });
            }
            return null;
          })}
        </ul>
      )}
    </div>
  );
};

interface SelectItemProps {
  value: string;
  children: React.ReactNode;
  onClick?: () => void;
}

export const SelectItem: React.FC<SelectItemProps> = ({ value, children, onClick }) => {
  return (
    <li
      className="p-2 hover:bg-zinc-700 cursor-pointer"
      onClick={onClick}
      data-value={value}
    >
      {children}
    </li>
  );
};

interface Chat {
  id: string
  name: string
  messages: any[]
  vehicleId: string
  public: boolean
  createdAt: number
  updatedAt: number
}

interface SidebarProps {
  chats: Chat[]
  currentChatId: string
  onSelectChat: (chatId: string) => void
  onCreateNewChat: () => void
  onDeleteChat: (chatId: string) => void
  onRenameChat: (chatId: string, newName: string) => void
  onShareChat?: (chatId: string) => void | Promise<void>
  onCopyPublicUrl?: (chatId: string) => void | Promise<void>
  onStopSharing?: (chatId: string) => void | Promise<void>
  showSidebar: boolean
  onToggleSidebar: () => void
  onAddVehicle: () => void
  isPreviewMode?: boolean
  previewVehicleName?: string
}

export function Sidebar({
  chats,
  currentChatId,
  onSelectChat,
  onCreateNewChat,
  onDeleteChat,
  onRenameChat,
  onShareChat,
  onCopyPublicUrl,
  onStopSharing,
  showSidebar,
  onToggleSidebar,
  onAddVehicle,
  isPreviewMode = false,
  previewVehicleName,
}: SidebarProps) {
  const [editingChatId, setEditingChatId] = useState<string | null>(null);
  const [editingName, setEditingName] = useState("");
  const [showManageVehicles, setShowManageVehicles] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const [chatToDelete, setChatToDelete] = useState<{ id: string; name: string } | null>(null);
  const [menuOpenForChatId, setMenuOpenForChatId] = useState<string | null>(null);
  const [shareChatId, setShareChatId] = useState<string | null>(null);
  const { signOut, isAuthenticated } = useAuth();
  const { selectedVehicle, setSelectedVehicle, vehicles } = useVehicle();
  const navigate = useNavigate();

  // Group chats by vehicle and sort by newest first within each group
  const groupedChats = React.useMemo(() => {
    const groups: { [vehicleId: string]: Chat[] } = {};
    
    chats.forEach(chat => {
      if (!groups[chat.vehicleId]) {
        groups[chat.vehicleId] = [];
      }
      groups[chat.vehicleId].push(chat);
    });

    // Sort chats within each group by newest first (updatedAt or createdAt)
    Object.keys(groups).forEach(vehicleId => {
      groups[vehicleId].sort((a, b) => (b.updatedAt || b.createdAt) - (a.updatedAt || a.createdAt));
    });

    return groups;
  }, [chats]);

  // Get vehicle name by ID
  const getVehicleName = (vehicleId: string) => {
    const vehicle = vehicles.find(v => v.id === vehicleId);
    return vehicle ? vehicle.name : 'Unknown Vehicle';
  };

  const startEditing = (chatId: string, currentName: string) => {
    setEditingChatId(chatId);
    setEditingName(currentName);
  };

  const saveEdit = () => {
    if (editingChatId && editingName.trim()) {
      onRenameChat(editingChatId, editingName);
      setEditingChatId(null);
    }
  };

  const cancelEdit = () => {
    setEditingChatId(null);
  };

  const handleDeleteChat = () => {
    if (chatToDelete) {
      onDeleteChat(chatToDelete.id);
      setChatToDelete(null);
    }
  };

  return (
    <>
      {/* Mobile overlay */}
      {showSidebar && <div className="fixed inset-0 bg-black/50 z-40 md:hidden" onClick={onToggleSidebar} />}

      {/* Sidebar */}
      <aside
        className={cn(
          "fixed md:relative inset-y-0 left-0 z-50 w-72 bg-zinc-800 border-r border-zinc-700 flex flex-col transition-transform duration-300 ease-in-out",
          showSidebar ? "translate-x-0" : "-translate-x-full md:translate-x-0",
        )}
      >
        {/* Logo header taking full width */}
        <div className="p-4 border-b border-zinc-700">
          <img 
            src="/img/garagebot-logo-3.png" 
            alt="GarageBot" 
            className="w-full h-auto"
          />
        </div>

        {isPreviewMode ? (
          <>
            {/* Preview Mode Content */}
            <div className="p-4 border-b border-zinc-700">
              <div className="px-3 py-2 bg-emerald-500/10 border border-emerald-500/20 rounded-md">
                <p className="text-emerald-400 text-sm font-medium mb-1">Preview Mode</p>
                <p className="text-emerald-300 text-xs">
                  {previewVehicleName ? `Chat: ${previewVehicleName}` : "Select a vehicle to start chatting"}
                </p>
              </div>
            </div>

            <div className="flex-1 p-4">
              <div className="text-center text-zinc-400">
                <p className="text-sm mb-4">
                  You're using GarageBot in preview mode. {!isAuthenticated ? "Sign up to save your conversations and access more features!" : "Visit your dashboard to access all your saved conversations and features."}
                </p>
                
                <div className="space-y-2">
                  {!isAuthenticated ? (
                    <>
                      <Button
                        onClick={() => navigate('/register')}
                        className="w-full bg-emerald-600 hover:bg-emerald-700 text-white"
                      >
                        Create Account
                      </Button>
                      <Button
                        onClick={() => navigate('/login')}
                        variant="outline"
                        className="w-full"
                      >
                        Sign In
                      </Button>
                    </>
                  ) : (
                    <Button
                      onClick={() => navigate('/')}
                      className="w-full bg-emerald-600 hover:bg-emerald-700 text-white"
                    >
                      Dashboard
                    </Button>
                  )}
                </div>
              </div>
            </div>

            {/* Footer */}
            <div className="p-4 border-t border-zinc-700 flex items-center justify-between">
              {!isAuthenticated ? (
                <button
                  onClick={() => navigate('/login')}
                  className="text-xs text-emerald-500 hover:text-emerald-400"
                >
                  Sign In
                </button>
              ) : (
                <button
                  onClick={() => navigate('/')}
                  className="text-xs text-emerald-500 hover:text-emerald-400"
                >
                  Dashboard
                </button>
              )}
            </div>
          </>
        ) : (
          <>
            {/* Authenticated Mode Content */}
            {/* Vehicle Selector */}
            <div className="p-4 border-b border-zinc-700">
              <div className="space-y-2">
                <Select value={selectedVehicle?.name} onValueChange={(id: string) => {
                  const vehicle = vehicles.find((v) => v.id === id);
                  if (vehicle) setSelectedVehicle(vehicle);
                }}>
                  {vehicles.map((vehicle) => (
                    <SelectItem key={vehicle.id} value={vehicle.id}>
                      {vehicle.name}
                    </SelectItem>
                  ))}
                </Select>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={onAddVehicle}
                  className="flex items-center text-emerald-500 hover:text-emerald-400 hover:bg-zinc-800 justify-start px-2 h-7 w-full"
                >
                  <PlusCircle />
                  Add Vehicle
                </Button>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setShowManageVehicles(true)}
                  className="flex items-center text-zinc-400 hover:text-emerald-400 hover:bg-zinc-800 justify-start px-2 h-7 w-full"
                >
                  <Edit />
                  Manage Vehicles
                </Button>
              </div>
            </div>

            <div className="p-4">
              <Button
                onClick={onCreateNewChat}
                className="w-full bg-emerald-600 hover:bg-emerald-700 text-white flex items-center justify-center"
              >
                <PlusCircle />
                <span>New Chat</span>
              </Button>
            </div>

            <ScrollArea className="flex-1">
              <div className="p-2 space-y-4">
                {Object.keys(groupedChats).length === 0 ? (
                  <div className="flex flex-col items-center justify-center py-8 text-center">
                    <p className="text-zinc-400 text-sm">No chats yet</p>
                    <p className="text-zinc-500 text-xs mt-1">Create a new chat to get started</p>
                  </div>
                ) : (
                  Object.keys(groupedChats).map((vehicleId) => (
                    <div key={vehicleId} className="space-y-1">
                      {/* Vehicle section header */}
                      <div className="px-3 py-2 text-xs font-medium text-zinc-400 uppercase tracking-wider">
                        {getVehicleName(vehicleId)}
                      </div>
                      
                      {/* Chats for this vehicle */}
                      {groupedChats[vehicleId].map((chat) => (
                        <div
                          key={chat.id}
                          className={cn(
                            "group flex items-center justify-between p-3 rounded-lg ml-2",
                            currentChatId === chat.id ? "bg-zinc-700" : "hover:bg-zinc-700/50",
                          )}
                        >
                          {editingChatId === chat.id ? (
                            <div className="flex items-center w-full space-x-2">
                              <input
                                type="text"
                                value={editingName}
                                onChange={(e) => setEditingName(e.target.value)}
                                className="flex-1 bg-zinc-600 text-white border-none rounded p-1 text-sm focus:ring-1 focus:ring-emerald-500 outline-none"
                                autoFocus
                                onKeyDown={(e) => {
                                  if (e.key === "Enter") saveEdit();
                                  if (e.key === "Escape") cancelEdit();
                                }}
                              />
                              <Button variant="ghost" size="icon" onClick={saveEdit} className="h-7 w-7">
                                <Check />
                              </Button>
                              <Button variant="ghost" size="icon" onClick={cancelEdit} className="h-7 w-7">
                                <X />
                              </Button>
                            </div>
                          ) : (
                            <>
                              <button className="flex-1 text-left truncate" onClick={() => onSelectChat(chat.id)}>
                                {chat.name}
                              </button>
                              <div className="relative flex items-center opacity-0 group-hover:opacity-100 transition-opacity">
                                <Button
                                  variant="ghost"
                                  size="icon"
                                  className="h-7 w-7"
                                  onClick={(e: any) => {
                                    e.stopPropagation();
                                    setMenuOpenForChatId((prev) => (prev === chat.id ? null : chat.id));
                                  }}
                                >
                                  <MoreVertical />
                                </Button>

                                {menuOpenForChatId === chat.id && (
                                  <div className="absolute right-0 top-7 z-20 w-40 bg-zinc-800 border border-zinc-700 rounded shadow-lg">
                                    <button
                                      className="w-full text-left px-3 py-2 text-sm hover:bg-zinc-700 flex items-center gap-2"
                                      onClick={(e) => { e.stopPropagation(); setMenuOpenForChatId(null); startEditing(chat.id, chat.name); }}
                                    >
                                      <Edit className="h-4 w-4" /> Edit Name
                                    </button>
                                    
                                    {chat.public ? (
                                      <>
                                        <button
                                          className="w-full text-left px-3 py-2 text-sm hover:bg-zinc-700 flex items-center gap-2"
                                          onClick={async (e) => {
                                            e.stopPropagation();
                                            setMenuOpenForChatId(null);
                                            try {
                                              if (onCopyPublicUrl) {
                                                await onCopyPublicUrl(chat.id);
                                              }
                                            } catch (error) {
                                              console.error('Error copying URL:', error);
                                            }
                                          }}
                                        >
                                          <Copy className="h-4 w-4" /> Copy Public URL
                                        </button>
                                        <button
                                          className="w-full text-left px-3 py-2 text-sm hover:bg-zinc-700 flex items-center gap-2"
                                          onClick={async (e) => {
                                            e.stopPropagation();
                                            setMenuOpenForChatId(null);
                                            try {
                                              if (onStopSharing) {
                                                await onStopSharing(chat.id);
                                              }
                                            } catch (error) {
                                              console.error('Error stopping sharing:', error);
                                            }
                                          }}
                                        >
                                          <X className="h-4 w-4" /> Stop Sharing
                                        </button>
                                      </>
                                    ) : (
                                      <button
                                        className="w-full text-left px-3 py-2 text-sm hover:bg-zinc-700 flex items-center gap-2"
                                        onClick={async (e) => {
                                          e.stopPropagation();
                                          setMenuOpenForChatId(null);
                                          try {
                                            if (onShareChat) {
                                              await onShareChat(chat.id);
                                            }
                                          } finally {
                                            setShareChatId(chat.id);
                                          }
                                        }}
                                      >
                                        <Share className="h-4 w-4" /> Share
                                      </button>
                                    )}
                                    
                                    <button
                                      className="w-full text-left px-3 py-2 text-sm hover:bg-zinc-700 text-red-400 hover:text-red-300 flex items-center gap-2"
                                      onClick={(e) => { e.stopPropagation(); setMenuOpenForChatId(null); setChatToDelete({ id: chat.id, name: chat.name }); }}
                                    >
                                      <Trash className="h-4 w-4" /> Delete
                                    </button>
                                  </div>
                                )}
                              </div>
                            </>
                          )}
                        </div>
                      ))}
                    </div>
                  ))
                )}
              </div>
            </ScrollArea>

            <div className="p-4 border-t border-zinc-700 flex items-center justify-between">
              <button
                onClick={() => signOut()}
                className="text-xs text-emerald-500 hover:text-emerald-400"
              >
                Logout
              </button>
            </div>
          </>
        )}
      </aside>

      {!isPreviewMode && (
        <>
          <ManageVehiclesDialog open={showManageVehicles} onOpenChange={setShowManageVehicles} onAddVehicle={onAddVehicle} />
          <SettingsDialog open={showSettings} onOpenChange={setShowSettings} />
          <ConfirmationDialog
            open={!!chatToDelete}
            onOpenChange={() => setChatToDelete(null)}
            title="Delete Chat"
            description={`Are you sure you want to delete "${chatToDelete?.name}"?`}
            confirmText="Delete"
            cancelText="Cancel"
            onConfirm={handleDeleteChat}
            destructive={true}
          />
          {/* Share dialog */}
          <ShareChatDialog
            open={!!shareChatId}
            onOpenChange={(open) => setShareChatId(open ? shareChatId : null)}
            chatId={shareChatId}
          />
        </>
      )}

      {/* Mobile close button floating outside sidebar */}
      <Button
        variant="outline"
        size="icon"
        onClick={onToggleSidebar}
        className={cn(
          "fixed top-4 right-4 z-40 md:hidden bg-zinc-800 border-zinc-700 transition-opacity duration-300",
          showSidebar ? "opacity-100" : "opacity-0 pointer-events-none"
        )}
      >
        <X />
      </Button>
    </>
  );
}
